'use client';

import { useCallback, useState, type FormEvent } from 'react';

import { useTranslations } from 'next-intl';

import { buildTrackingParams } from '~/lib/tracking/tracking-params';

import { submitFallbackLeadAction } from './_lib/server/submit-fallback-lead.action';

type Status = 'idle' | 'submitting' | 'success' | 'error';

const INTEREST_OPTIONS = [
  'leads',
  'ads',
  'studio',
  'full_pipeline',
  'unsure',
] as const;

/** Null-safe PostHog capture (posthog-js attaches to window.posthog after init). */
function capture(event: string, props?: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  (
    window as unknown as {
      posthog?: { capture?: (e: string, p?: Record<string, unknown>) => void };
    }
  ).posthog?.capture?.(event, props);
}

function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie
    .split('; ')
    .find((c) => c.startsWith(`${name}=`));
  return match?.split('=')[1];
}

/** Read a URL query param client-side (avoids useSearchParams' Suspense/dynamic-render requirement). */
function readParam(name: string): string | undefined {
  if (typeof window === 'undefined') return undefined;
  return new URLSearchParams(window.location.search).get(name) ?? undefined;
}

/**
 * A4 — "Not ready to chat?" fallback lead form for the Selenos landing.
 *
 * Email-only async capture for visitors who won't start a live chat. Feeds the
 * same pipeline as the widget via submitFallbackLeadAction (selenos vertical).
 * The interest select maps to the `engineInterest` scoring signal.
 */
export function FallbackLeadForm() {
  const t = useTranslations('selenos');

  const [businessName, setBusinessName] = useState('');
  const [email, setEmail] = useState('');
  const [interest, setInterest] = useState('');
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState<Status>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const onSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (status === 'submitting' || !consent) return;

      setStatus('submitting');
      setErrorMsg('');
      capture('selenos_fallback_form_submitted');

      const res = await submitFallbackLeadAction({
        businessName: businessName.trim(),
        email: email.trim(),
        engineInterest: interest || undefined,
        consentGiven: true,
        consentText: t('fallbackConsent'),
        ...buildTrackingParams(readParam),
        fbp: readCookie('_fbp'),
      });

      if (res.success) {
        setStatus('success');
        capture('selenos_fallback_lead_captured');
      } else {
        setStatus('error');
        setErrorMsg(res.error ?? t('fallbackError'));
      }
    },
    [status, consent, businessName, email, interest, t],
  );

  const fieldClass =
    'w-full rounded-md border border-[var(--sel-line)] bg-[var(--sel-bg)] px-3.5 py-2.5 text-sm text-[var(--sel-ink)] placeholder:text-[var(--sel-faint)] focus:border-[var(--sel-accent)] focus:outline-none';

  if (status === 'success') {
    return (
      <div className="mx-auto mt-10 max-w-[420px] rounded-xl border border-[var(--sel-line)] bg-[var(--sel-card)] p-6 text-center">
        <p className="text-sm leading-relaxed text-[var(--sel-ink)]">
          {t('fallbackSuccess')}
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto mt-12 max-w-[420px] rounded-xl border border-[var(--sel-line)] bg-[var(--sel-card)] p-6 text-left">
      <h3 className="text-base font-semibold text-[var(--sel-ink)]">
        {t('fallbackTitle')}
      </h3>
      <p className="mt-1.5 text-sm leading-relaxed text-[var(--sel-muted)]">
        {t('fallbackBody')}
      </p>

      <form onSubmit={onSubmit} className="mt-5 flex flex-col gap-3">
        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-[var(--sel-muted)]">
            {t('fallbackBusinessName')}
          </span>
          <input
            type="text"
            required
            value={businessName}
            onChange={(e) => setBusinessName(e.target.value)}
            autoComplete="organization"
            className={fieldClass}
          />
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-[var(--sel-muted)]">
            {t('fallbackEmail')}
          </span>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            className={fieldClass}
          />
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-[var(--sel-muted)]">
            {t('fallbackInterest')}
          </span>
          <select
            value={interest}
            onChange={(e) => setInterest(e.target.value)}
            className={fieldClass}
          >
            <option value="">{t('fallbackInterestDefault')}</option>
            {INTEREST_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {t(`fallbackInterest_${opt}`)}
              </option>
            ))}
          </select>
        </label>

        <label className="mt-1 flex items-start gap-2.5 text-xs leading-relaxed text-[var(--sel-muted)]">
          <input
            type="checkbox"
            required
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--sel-accent)]"
          />
          <span>{t('fallbackConsent')}</span>
        </label>

        {status === 'error' ? (
          <p role="alert" className="text-xs text-red-700">
            {errorMsg || t('fallbackError')}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={status === 'submitting' || !consent}
          className="mt-2 inline-flex h-11 items-center justify-center rounded-md bg-[var(--sel-accent)] px-6 text-sm font-medium text-[var(--sel-accent-ink)] transition-colors hover:bg-[var(--sel-accent-strong)] focus-visible:ring-2 focus-visible:ring-[var(--sel-accent)] focus-visible:outline-none disabled:opacity-50"
        >
          {status === 'submitting'
            ? t('fallbackSubmitting')
            : t('fallbackSubmit')}
        </button>
      </form>
    </div>
  );
}
